import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  LuArrowRight,
  LuArrowLeft,
  LuCheck,
  LuCopy,
  LuMinus,
  LuPlus,
  LuDownload,
  LuChevronDown,
  LuChevronUp,
  LuGift,
  LuX,
} from 'react-icons/lu';
import { FaTelegram } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import { Icon } from '../lib/icons';
import toast from 'react-hot-toast';
import api from '../lib/api';
import styles from './Checkout.module.css';

// Simplified flat pricing: $2.50/account, $2.00 for 30+ accounts
const pricingTiers = [
  { min: 1, max: 29, price: 2.50 },
  { min: 30, max: Infinity, price: 2.00 },
];

function getUnitPrice(qty: number): number {
  const tier = pricingTiers.find(t => qty >= t.min && qty <= t.max);
  return tier ? tier.price : pricingTiers[0].price;
}

// Available currencies
// Note: TRC20 requires minimum 10 accounts order
const currencies = [
  { id: 'usdc', label: 'USDC', networks: ['BEP20', 'ERC20', 'SOL'] },
  { id: 'usdt', label: 'USDT', networks: ['BEP20', 'ERC20', 'TRC20', 'SOL'] },
  { id: 'eth', label: 'ETH', networks: ['Ethereum'] },
  { id: 'btc', label: 'BTC', networks: ['Bitcoin'] },
];

// Map currency + network to NOWPayments currency code
const getCurrencyCode = (currency: string, network: string): string => {
  // USDC mappings
  if (currency === 'usdc' && network === 'BEP20') return 'usdcbsc';
  if (currency === 'usdc' && network === 'ERC20') return 'usdc';
  if (currency === 'usdc' && network === 'SOL') return 'usdcsol';
  
  // USDT mappings
  if (currency === 'usdt' && network === 'BEP20') return 'usdtbsc';
  if (currency === 'usdt' && network === 'ERC20') return 'usdterc20';
  if (currency === 'usdt' && network === 'TRC20') return 'usdttrc20';
  if (currency === 'usdt' && network === 'SOL') return 'usdtsol';
  
  // Other currencies
  if (currency === 'eth') return 'eth';
  if (currency === 'btc') return 'btc';
  
  return currency;
};

const Checkout: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { orderNumber: urlOrderNumber } = useParams<{ orderNumber?: string }>();

  // Step state
  const [step, setStep] = useState(1);

  // Order config
  const [country, setCountry] = useState(searchParams.get('country') || 'FR');
  const [quantity, setQuantity] = useState(Number(searchParams.get('qty')) || 1);

  // User info
  const [email, setEmail] = useState('');
  
  // Loading state for abandoned cart recovery
  const [loadingExistingOrder, setLoadingExistingOrder] = useState(false);

  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [validPromoCode, setValidPromoCode] = useState<{code: string; discountPercent: number} | null>(null);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [validatingPromoCode, setValidatingPromoCode] = useState(false);

  // Payment
  const [selectedCurrency, setSelectedCurrency] = useState('usdc');
  const [selectedNetwork, setSelectedNetwork] = useState('BEP20');
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [paymentAddress, setPaymentAddress] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentCurrency, setPaymentCurrency] = useState('');
  const [copied, setCopied] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentCreating, setPaymentCreating] = useState(false);
  const [deliveryToken, setDeliveryToken] = useState('');
  const [credentials, setCredentials] = useState<Array<{username: string; password: string; twoFA: string}>>([]);
  const [showCredentials, setShowCredentials] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Computed pricing
  const unitPrice = getUnitPrice(quantity);
  const subtotal = unitPrice * quantity;
  const discount = validPromoCode ? subtotal * (validPromoCode.discountPercent / 100) : 0;
  const total = subtotal - discount;

  const handleQuantityChange = (val: number) => {
    if (val >= 1 && val <= 100) setQuantity(val);
  };

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Load existing order if orderNumber is in URL (abandoned cart recovery)
  useEffect(() => {
    if (!urlOrderNumber) return;

    const loadExistingOrder = async () => {
      setLoadingExistingOrder(true);
      try {
        const res = await api.get(`/orders/status/${urlOrderNumber}`);
        const order = res.data.order;
        
        if (order && order.status === 'PENDING_PAYMENT') {
          // Pre-fill order data
          setCountry(order.country);
          setQuantity(order.quantity);
          setOrderNumber(order.orderNumber);
          setOrderId(order.id || '');
          
          // Show message to user
          toast.success(t('checkout.orderRecovered') || 'Your order has been recovered! Please complete your payment.', {
            duration: 5000,
          });
          
          // Go to step 2 (email entry) so user can verify and proceed to payment
          setStep(2);
        } else if (order && order.status !== 'PENDING_PAYMENT') {
          toast.error(t('checkout.orderAlreadyProcessed') || 'This order has already been processed.');
        }
      } catch (error: any) {
        console.error('Failed to load existing order:', error);
        toast.error(t('checkout.orderNotFound') || 'Order not found. Please create a new one.');
      } finally {
        setLoadingExistingOrder(false);
      }
    };

    loadExistingOrder();
  }, [urlOrderNumber, t]);


  // Create payment via NOWPayments API
  const createPayment = async (oId: string, crypto: string) => {
    setPaymentCreating(true);
    setPaymentAddress('');
    setPaymentAmount('');
    try {
      const res = await api.post('/payments/create', {
        orderId: oId,
        payCurrency: crypto,
      });
      const payment = res.data.payment;
      setPaymentAddress(payment.payAddress || '');
      setPaymentAmount(String(payment.payAmount || ''));
      setPaymentCurrency(payment.payCurrency || crypto);

      // Start polling for payment confirmation
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/payments/status/${payment.paymentId}`);
          const status = statusRes.data.payment?.paymentStatus;
          if (status === 'confirmed' || status === 'finished') {
            if (pollingRef.current) clearInterval(pollingRef.current);
            
            // Fetch order details to get delivery token
            try {
              const orderRes = await api.get(`/orders/status/${orderNumber}?email=${email}`);
              const token = orderRes.data.order?.deliveryToken;
              if (token) {
                setDeliveryToken(token);
              }
            } catch {
              // Silently ignore if token not ready yet
            }
            
            setStep(4);
          }
        } catch {
          // Silently ignore polling errors
        }
      }, 15000);
    } catch (error: any) {
      let errorMessage = t('common.error');
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timeout - please try another payment method';
      } else if (error.response?.data?.error) {
        const apiError = error.response.data.error;
        
        // Handle minimum amount error specifically
        if (apiError.includes('less than minimal') || apiError.includes('minimum')) {
          errorMessage = t('checkout.minimumAmountError', { 
            network: selectedNetwork,
            defaultValue: `The minimum payment amount for ${selectedNetwork} is higher than your order total. Please select another payment method (BEP20 or ERC20 recommended).`
          });
        } else {
          errorMessage = apiError;
        }
      }
      
      toast.error(errorMessage, { duration: 8000 });
    } finally {
      setPaymentCreating(false);
    }
  };

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setValidPromoCode(null);
      setPromoCodeError('');
      return;
    }
    
    setValidatingPromoCode(true);
    setPromoCodeError('');
    try {
      const res = await api.get(`/promo-codes/validate?code=${encodeURIComponent(promoCode)}`);
      if (res.data.valid) {
        setValidPromoCode(res.data.promoCode);
        toast.success(t('checkout.promoCodeApplied'));
      }
    } catch (error: any) {
      setValidPromoCode(null);
      setPromoCodeError(error.response?.data?.error || t('checkout.invalidPromoCode'));
    } finally {
      setValidatingPromoCode(false);
    }
  };

  // Handle step navigation
  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (!email) {
        toast.error(t('checkout.emailRequired'));
        return;
      }
      setLoading(true);
      try {
        // Create order via API
        const res = await api.post('/orders', {
          email,
          quantity,
          country,
          withAccount: true,
          promoCode: validPromoCode ? validPromoCode.code : undefined,
        });
        const order = res.data.order;
        setOrderNumber(order.orderNumber || 'OFM-XXXXX');
        setOrderId(order.id || '');
        setStep(3);
        // Don't auto-create payment - let user select crypto/network first
      } catch {
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    }
  };


  // Copy payment address to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(paymentAddress);
    setCopied(true);
    toast.success(t('checkout.copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  // Copy payment amount to clipboard
  const handleCopyAmount = () => {
    navigator.clipboard.writeText(paymentAmount);
    setCopiedAmount(true);
    toast.success(t('checkout.copied'));
    setTimeout(() => setCopiedAmount(false), 2000);
  };

  // Fetch credentials when delivery token is available
  const fetchCredentials = async (token: string) => {
    if (!token) return;
    setLoadingCredentials(true);
    try {
      const res = await api.get(`/delivery/credentials/${token}`);
      setCredentials(res.data.credentials || []);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoadingCredentials(false);
    }
  };

  // Copy credential text to clipboard
  const handleCopyCredential = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('checkout.copied'));
  };

  // Get current currency info
  const currentCurrency = currencies.find(c => c.id === selectedCurrency) || currencies[0];
  
  // Check if TRC20 is selected but quantity is below minimum (10 accounts)
  const isTrc20BelowMinimum = selectedCurrency === 'usdt' && selectedNetwork === 'TRC20' && quantity < 10;

  // Handle currency selection
  const handleCurrencyChange = (currencyId: string) => {
    // Only allow change if payment not yet created
    if (paymentAddress) {
      toast.error(t('checkout.cannotChangeAfterPayment', {
        defaultValue: 'Cannot change payment method after address is generated. Go back to restart.'
      }));
      return;
    }
    
    setSelectedCurrency(currencyId);
    const currency = currencies.find(c => c.id === currencyId);
    if (currency && currency.networks.length > 0) {
      setSelectedNetwork(currency.networks[0]);
    }
  };

  // Handle network selection
  const handleNetworkChange = (network: string) => {
    // Only allow change if payment not yet created
    if (paymentAddress) {
      toast.error(t('checkout.cannotChangeAfterPayment', {
        defaultValue: 'Cannot change payment method after address is generated. Go back to restart.'
      }));
      return;
    }
    
    setSelectedNetwork(network);
  };

  const stepLabels = [t('checkout.step1'), t('checkout.step2'), t('checkout.step3'), t('checkout.step4')];

  // Show loader while loading existing order
  if (loadingExistingOrder) {
    return (
      <div className={styles.checkout}>
        <div className={styles.checkoutInner}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ color: 'var(--text-secondary)' }}
            >
              <p style={{ fontSize: '1.1rem', marginBottom: '12px' }}>
                {t('checkout.loadingOrder') || 'Loading your order...'}
              </p>
              <div className={styles.spinner} style={{ margin: '24px auto' }}></div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checkout}>
      <div className={styles.checkoutInner}>
        {/* Step Indicator */}
        <div className={styles.steps}>
          {stepLabels.map((label, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className={`${styles.stepDivider} ${step > i ? styles.completed : ''}`} />}
              <div className={styles.stepItem}>
                <div className={`${styles.stepCircle} ${step > i + 1 ? styles.completed : step === i + 1 ? styles.active : ''}`}>
                  {step > i + 1 ? <Icon icon={LuCheck} size={12} strokeWidth={3} /> : i + 1}
                </div>
                <span className={`${styles.stepLabel} ${step === i + 1 ? styles.active : ''}`}>{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <motion.div className={styles.card} key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          {/* STEP 1 — Configuration */}
          {step === 1 && (
            <>
              <div className={styles.optionGroup}>
                <span className={styles.optionLabel}>{t('checkout.country')}</span>
                <div className={styles.countryGrid}>
                  {['FR', 'US'].map((c) => (
                    <button key={c} className={`${styles.countryOption} ${country === c ? styles.selected : ''}`} onClick={() => setCountry(c)}>
                      {c === 'FR' ? t('checkout.countryFR') : t('checkout.countryUS')}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.optionGroup}>
                <span className={styles.optionLabel}>{t('checkout.quantityLabel')}</span>
                <div className={styles.quantityControl}>
                  <button className={styles.quantityBtn} onClick={() => handleQuantityChange(quantity - 1)} disabled={quantity <= 1}>
                    <Icon icon={LuMinus} size={15} strokeWidth={2} />
                  </button>
                  <input type="number" className={styles.quantityInput} value={quantity} onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)} min={1} max={100} />
                  <button className={styles.quantityBtn} onClick={() => handleQuantityChange(quantity + 1)} disabled={quantity >= 100}>
                    <Icon icon={LuPlus} size={15} strokeWidth={2} />
                  </button>
                </div>
                <div className={styles.tierIndicators}>
                  {pricingTiers.map((tier, i) => (
                    <div key={i} className={`${styles.tierBadge} ${quantity >= tier.min && quantity <= tier.max ? styles.activeTier : ''}`}>
                      {tier.max === Infinity ? `${tier.min}+` : `${tier.min}-${tier.max}`}
                      <span>${tier.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.summary}>
                <div className={styles.summaryRow}><span>{t('checkout.quantity')}</span><span>{quantity}</span></div>
                <div className={`${styles.summaryRow} ${styles.total}`}><span>{t('checkout.totalPrice')}</span><span>${total.toFixed(2)}</span></div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnPrimary} onClick={handleNext}>
                  {t('checkout.next')} <Icon icon={LuArrowRight} size={14} strokeWidth={2.5} />
                </button>
              </div>

              {/* Quality Explanation Section */}
              <div className={styles.qualitySection}>
                <h3 className={styles.qualityTitle}>{t('checkout.whyThisPrice')}</h3>
                
                <div className={styles.qualityProblem}>
                  <div className={styles.qualityIcon}>
                    <Icon icon={LuX} size={14} strokeWidth={2.5} />
                  </div>
                  <div className={styles.qualityContent}>
                    <div className={styles.qualityLabel}>{t('checkout.qualityProblem')}</div>
                    <div className={styles.qualityDesc}>{t('checkout.qualityProblemDesc')}</div>
                  </div>
                </div>

                <div className={styles.qualityGrid}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div key={i} className={`${styles.qualityItem} ${i > 6 ? styles.desktopOnly : ''}`}>
                      <div className={styles.qualityIconSuccess}>
                        <Icon icon={LuCheck} size={12} strokeWidth={3} />
                      </div>
                      <span>{t(`checkout.qualitySolution${i}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* STEP 2 — Email and promo code */}
          {step === 2 && (
            <>
              <div className={styles.formGroup}>
                <label>{t('checkout.email')}</label>
                <input
                  type="email"
                  className={styles.input}
                  placeholder={t('checkout.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <small>{t('checkout.emailRequired')}</small>
              </div>

              <div className={styles.formGroup}>
                <label>{t('checkout.promoCode')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder={t('checkout.promoCodePlaceholder')}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoCodeError('');
                      setValidPromoCode(null);
                    }}
                    style={{ flex: 1 }}
                  />
                  <button
                    className={styles.btnSecondary}
                    onClick={validatePromoCode}
                    disabled={!promoCode.trim() || validatingPromoCode}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {validatingPromoCode ? t('checkout.processing') : t('checkout.applyPromoCode')}
                  </button>
                </div>
                {promoCodeError && <small style={{ color: 'var(--error-color, #ef4444)' }}>{promoCodeError}</small>}
                {validPromoCode && <small style={{ color: 'var(--success-color, #10b981)' }}>{t('checkout.promoCodeApplied')}: {validPromoCode.discountPercent}% {t('checkout.discount').toLowerCase()}</small>}
              </div>

              <div className={styles.summary}>
                <div className={styles.summaryRow}>
                  <span>{country === 'FR' ? t('checkout.countryFR') : t('checkout.countryUS')} - {quantity} {quantity === 1 ? t('pricing.account') : t('pricing.accounts')}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>{t('checkout.subtotal')}</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {validPromoCode && (
                  <div className={styles.summaryRow} style={{ color: 'var(--success-color, #10b981)' }}>
                    <span>{t('checkout.discount')} ({validPromoCode.code})</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.total}`}>
                  <span>{t('checkout.totalPrice')}</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnSecondary} onClick={() => setStep(1)}>
                  <Icon icon={LuArrowLeft} size={14} strokeWidth={2} /> {t('checkout.back')}
                </button>
                <button className={styles.btnPrimary} onClick={handleNext} disabled={loading}>
                  {loading ? t('checkout.processing') : t('checkout.next')} {!loading && <Icon icon={LuArrowRight} size={14} strokeWidth={2.5} />}
                </button>
              </div>
            </>
          )}

          {/* STEP 3 — Payment with crypto selector */}
          {step === 3 && (
            <>
              {/* Show selectors only when payment address NOT yet generated */}
              {!paymentAddress && !paymentCreating && (
                <>
                  {/* Currency selector */}
                  <div className={styles.optionGroup}>
                    <span className={styles.optionLabel}>{t('checkout.selectCurrency')}</span>
                    <div className={styles.currencyGrid}>
                      {currencies.map((currency) => (
                        <button
                          key={currency.id}
                          className={`${styles.currencyOption} ${selectedCurrency === currency.id ? styles.selected : ''}`}
                          onClick={() => handleCurrencyChange(currency.id)}
                        >
                          {currency.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Network selector (only if multiple networks available) */}
                  {currentCurrency.networks.length > 1 && (
                    <div className={styles.optionGroup}>
                      <span className={styles.optionLabel}>{t('checkout.selectNetwork')}</span>
                      <div className={styles.networkGrid}>
                        {currentCurrency.networks.map((network) => (
                          <button
                            key={network}
                            className={`${styles.networkOption} ${selectedNetwork === network ? styles.selected : ''}`}
                            onClick={() => handleNetworkChange(network)}
                          >
                            {network}
                          </button>
                        ))}
                      </div>
                      {isTrc20BelowMinimum && (
                        <small style={{ color: 'var(--error-color, #ef4444)', marginTop: '8px', display: 'block' }}>
                          {t('checkout.trc20NotAvailable', { 
                            defaultValue: 'TRC20 requires a minimum of $10. Available for orders of 4+ accounts.' 
                          })}
                        </small>
                      )}
                    </div>
                  )}
                </>
              )}

              {!paymentAddress && !paymentCreating && (
                <div className={styles.actions} style={{ marginTop: '24px' }}>
                  <button 
                    className={styles.btnPrimary} 
                    onClick={() => {
                      if (isTrc20BelowMinimum) {
                        toast.error(t('checkout.trc20NotAvailable', { 
                          defaultValue: 'TRC20 requires a minimum of $10. Available for orders of 4+ accounts.' 
                        }));
                        return;
                      }
                      const code = getCurrencyCode(selectedCurrency, selectedNetwork);
                      createPayment(orderId, code);
                    }}
                    disabled={isTrc20BelowMinimum}
                    style={isTrc20BelowMinimum ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {t('checkout.generatePaymentAddress', { defaultValue: 'Generate payment address' })}
                    <Icon icon={LuArrowRight} size={14} strokeWidth={2.5} />
                  </button>
                </div>
              )}

              {paymentCreating ? (
                <div className={styles.waitingStatus}>
                  <div className={styles.spinner} />
                  <h4>{t('checkout.processing')}</h4>
                </div>
              ) : paymentAddress ? (
                <>
                  {/* Payment Method Info with Back Icon */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    gap: '16px'
                  }}>
                    <button 
                      onClick={() => {
                        if (pollingRef.current) clearInterval(pollingRef.current);
                        setPaymentAddress('');
                        setPaymentAmount('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '8px',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        transition: 'color 0.2s',
                        opacity: 0.7
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                    >
                      <Icon icon={LuArrowLeft} size={16} strokeWidth={2} />
                    </button>
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px',
                      fontSize: '14px',
                      color: 'var(--text-secondary)',
                      flex: 1,
                      justifyContent: 'center'
                    }}>
                      <div>
                        <span style={{ opacity: 0.6 }}>{t('checkout.selectCurrency')}:</span>
                        {' '}
                        <strong style={{ color: 'var(--text-primary)' }}>{currentCurrency.label}</strong>
                      </div>
                      <div style={{ opacity: 0.3 }}>•</div>
                      <div>
                        <span style={{ opacity: 0.6 }}>{t('checkout.selectNetwork')}:</span>
                        {' '}
                        <strong style={{ color: 'var(--text-primary)' }}>{selectedNetwork}</strong>
                      </div>
                    </div>
                    <div style={{ width: '32px' }}></div> {/* Spacer for balance */}
                  </div>

                  <div className={styles.paymentAmount}>
                    <p>{t('checkout.sendExactly')}</p>
                    <div className={styles.amountRow}>
                      <div>
                        <div className={styles.amount}>{paymentAmount} {currentCurrency.label}</div>
                      </div>
                      <button className={styles.copyBtnAmount} onClick={handleCopyAmount}>
                        <Icon icon={LuCopy} size={12} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Payment Warning */}
                  <div className={styles.paymentWarning}>
                    <p>{t('checkout.paymentWarning', { amount: paymentAmount, currency: currentCurrency.label })}</p>
                  </div>
                  
                  {/* Fee Notice */}
                  <div className={styles.feeNotice}>
                    <p>{t('checkout.processorFeeNotice')}</p>
                  </div>
                  
                  {/* QR Code Section */}
                  <div className={styles.qrCodeSection}>
                    <QRCodeSVG 
                      value={paymentAddress}
                      size={180}
                      level="H"
                      includeMargin={true}
                      className={styles.qrCode}
                    />
                  </div>
                  
                  <div className={styles.paymentAddress}>
                    <label>{t('checkout.toAddress')}</label>
                    <div className={styles.addressRow}>
                      <div className={styles.addressText}>{paymentAddress}</div>
                      <button className={styles.copyBtn} onClick={handleCopy}>
                        <Icon icon={LuCopy} size={12} strokeWidth={2} /> {copied ? t('checkout.copied') : t('checkout.copyAddress')}
                      </button>
                    </div>
                  </div>
                  <div className={styles.waitingStatus}>
                    <div className={styles.spinner} />
                    <h4>{t('checkout.waitingPayment')}</h4>
                    <p>{t('checkout.waitingDesc')}</p>
                  </div>
                </>
              ) : null}

              <div className={styles.actions}>
                <button className={styles.btnSecondary} onClick={() => {
                  if (pollingRef.current) clearInterval(pollingRef.current);
                  
                  // If payment address exists, just reset to allow changing network
                  if (paymentAddress) {
                    setPaymentAddress('');
                    setPaymentAmount('');
                    // Stay at step 3 to reselect network
                  } else {
                    // No payment yet, go back to step 2
                    setStep(2);
                  }
                }}>
                  <Icon icon={LuArrowLeft} size={14} strokeWidth={2} /> 
                  {paymentAddress ? t('checkout.changePaymentMethod', { defaultValue: 'Change payment method' }) : t('checkout.back')}
                </button>
              </div>
            </>
          )}

          {/* STEP 4 — Confirmation */}
          {step === 4 && (
            <div className={styles.confirmation}>
              <div className={styles.confirmIcon}>
                <Icon icon={LuCheck} size={24} strokeWidth={3} />
              </div>
              <h2>{t('checkout.orderConfirmed')}</h2>
              <p>{t('checkout.orderNumber')}</p>
              <div className={styles.orderNum}>{orderNumber}</div>
              <p>{t('checkout.orderDesc')}</p>
              
              {/* Telegram Promo Card */}
              <div className={styles.telegramPromoCard}>
                <h3>
                  <Icon icon={LuGift} size={20} strokeWidth={2} />
                  {t('checkout.telegramPromoTitle')}
                </h3>
                <p>{t('checkout.telegramPromoMessage')}</p>
                <a 
                  href="https://t.me/ofmigshopsupport" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.telegramPromoBtn}
                >
                  <Icon icon={FaTelegram} size={18} />
                  {t('checkout.telegramContact')}
                </a>
              </div>
              
              {/* Credentials Section */}
              {deliveryToken && (
                <div className={styles.credentialsSection}>
                  <div className={styles.credentialsHeader}>
                    <h3>{t('checkout.credentials')}</h3>
                    <a 
                      href={`${api.defaults.baseURL}/delivery/download/${deliveryToken}`}
                      className={styles.downloadBtn}
                      download
                    >
                      <Icon icon={LuDownload} size={12} strokeWidth={2} />
                      {t('checkout.downloadCredentials')}
                    </a>
                  </div>
                  
                  <button 
                    className={styles.toggleCredentials}
                    onClick={() => {
                      if (!showCredentials && credentials.length === 0) {
                        fetchCredentials(deliveryToken);
                      }
                      setShowCredentials(!showCredentials);
                    }}
                  >
                    <Icon icon={showCredentials ? LuChevronUp : LuChevronDown} size={14} strokeWidth={2} />
                    {showCredentials ? t('checkout.hideCredentials') : t('checkout.viewCredentials')}
                  </button>
                  
                  {showCredentials && (
                    loadingCredentials ? (
                      <div className={styles.waitingStatus}>
                        <div className={styles.spinner} />
                        <p>{t('common.loading')}</p>
                      </div>
                    ) : (
                      <div className={styles.credentialsList}>
                        {credentials.map((cred, index) => (
                          <div key={index} className={styles.credentialItem}>
                            <div className={styles.credentialRow}>
                              <span className={styles.credentialLabel}>Username</span>
                              <div className={styles.credentialValue}>{cred.username}</div>
                              <button 
                                className={styles.copyCredentialBtn}
                                onClick={() => handleCopyCredential(cred.username)}
                              >
                                <Icon icon={LuCopy} size={12} strokeWidth={2} />
                              </button>
                            </div>
                            <div className={styles.credentialRow}>
                              <span className={styles.credentialLabel}>Password</span>
                              <div className={styles.credentialValue}>{cred.password}</div>
                              <button 
                                className={styles.copyCredentialBtn}
                                onClick={() => handleCopyCredential(cred.password)}
                              >
                                <Icon icon={LuCopy} size={12} strokeWidth={2} />
                              </button>
                            </div>
                            {cred.twoFA && (
                              <div className={styles.credentialRow}>
                                <span className={styles.credentialLabel}>2FA</span>
                                <div className={styles.credentialValue}>{cred.twoFA}</div>
                                <button 
                                  className={styles.copyCredentialBtn}
                                  onClick={() => handleCopyCredential(cred.twoFA)}
                                >
                                  <Icon icon={LuCopy} size={12} strokeWidth={2} />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}
              
              <div className={styles.confirmDetails}>
                <div className={styles.summaryRow}><span>{t('checkout.country')}</span><span>{country === 'FR' ? t('checkout.countryFR') : t('checkout.countryUS')}</span></div>
                <div className={styles.summaryRow}><span>{t('checkout.quantity')}</span><span>{quantity}</span></div>
                <div className={styles.summaryRow}><span>{t('checkout.totalPrice')}</span><span>${total.toFixed(2)}</span></div>
                <div className={styles.summaryRow}><span>{t('checkout.estimatedDelivery')}</span><span>Instant</span></div>
              </div>
              <div className={styles.actions}>
                <Link to="/" className={styles.btnPrimary}>
                  {t('nav.home')} <Icon icon={LuArrowRight} size={14} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;
